// Part 2 (spec §6 N7/NB5): how the help sheet, which renders ABOVE
// GameFlowProvider, reaches the provider's authoritative apply path.
//
// React context only flows downwards, so SessionHelpProvider (Lane B) owns a
// registry and exposes its `registerGameFlow` through this setter context.
// GameFlowProvider (Lane F) calls it in an effect with
// `{ applyAuthoritative, isHydrating, stepIndex }`. The call returns an
// `unregister` that clears the registration only while it still holds that
// call's own token, so an old provider instance cleaning up after a newer one
// registered (StrictMode double effects, a keyed remount) cannot wipe the newer
// registration.
//
// Lane B: provide `registerGameFlow` as the value of GameFlowRegistryContext.
// `createGameFlowRegistry()` below is a ready-made implementation with the
// token rule; use it or match its behaviour. Outside a registry provider the
// context is null and GameFlowProvider registers nothing.
//
// `applyAuthoritative(row, { newRun })` → Promise<boolean>:
//   - pass the `live` row returned by the sheet's own advance_game_step or
//     restart_question call. It applies only when the row is for this phone's
//     run and at least as new as the version the phone holds (N2); a stale row
//     resolves false and changes nothing;
//   - pass `{ newRun: true }` for a row that starts a new run (outcome
//     `new_run`, or `reset` from restart_session_run). It is applied the way a
//     reset is: it may move the phone backwards, and old-run queue entries are
//     purged.

import { createContext } from 'react';

export const GameFlowRegistryContext = createContext(null);

/**
 * A token-scoped registry. `register(api)` stores `api` and returns an
 * `unregister` bound to that call; `get()` returns the current api or null;
 * `subscribe(fn)` is told on every change.
 */
export function createGameFlowRegistry() {
  let current = null; // { token, api }
  let seq = 0;
  const listeners = new Set();
  const notify = () => listeners.forEach((fn) => { try { fn(current?.api ?? null); } catch { /* observer */ } });
  return {
    register(api) {
      const token = ++seq;
      current = { token, api };
      notify();
      return () => {
        if (current && current.token === token) {
          current = null;
          notify();
        }
      };
    },
    get() {
      return current?.api ?? null;
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
